/**
 * Realtime SSE client.
 *
 * Connects to /api/events (Server-Sent Events) once per logged-in session and
 * re-emits every server change as a ``CustomEvent('rf-data-change', {detail})``
 * on window. Any page can subscribe.
 */
let _es = null;
let _reconnectTimer = null;
let _shouldReconnect = false;
let _currentToken = null;

const sseUrl = (token) => {
  const backend = process.env.REACT_APP_BACKEND_URL || "";
  return `${backend.replace(/\/$/, "")}/api/events?token=${encodeURIComponent(token)}`;
};

const _clearTimers = () => {
  if (_reconnectTimer) { clearTimeout(_reconnectTimer); _reconnectTimer = null; }
};

const _open = () => {
  if (!_currentToken) return;
  try { _es && _es.close(); } catch { /* ignore */ }
  try {
    _es = new EventSource(sseUrl(_currentToken));
  } catch {
    _scheduleReconnect();
    return;
  }
  _es.onmessage = (ev) => {
    if (!ev.data) return;
    try {
      const msg = JSON.parse(ev.data);
      if (msg?.entity && msg.entity !== "system") {
        window.dispatchEvent(new CustomEvent("rf-data-change", { detail: msg }));
      }
    } catch { /* ignore non-JSON */ }
  };
  _es.onerror = () => {
    // EventSource auto-reconnects, but if the token became invalid we need
    // to close and back off manually.
    if (_es && _es.readyState === 2 /* CLOSED */) {
      _es = null;
      if (_shouldReconnect) _scheduleReconnect();
    }
  };
};

const _scheduleReconnect = () => {
  if (!_shouldReconnect) return;
  if (_reconnectTimer) return;
  _reconnectTimer = setTimeout(() => {
    _reconnectTimer = null;
    _open();
  }, 4000);
};

export const startRealtime = (token) => {
  if (!token) return;
  _shouldReconnect = true;
  _currentToken = token;
  if (_es && _es.readyState !== 2) return;
  _open();
};

export const stopRealtime = () => {
  _shouldReconnect = false;
  _currentToken = null;
  _clearTimers();
  try { _es && _es.close(); } catch { /* ignore */ }
  _es = null;
};
